// piano-frontend/src/components/GuideCoachmark.js
// 기능 안내 코치마크 — 우측 상단 사용자 메뉴(아바타/더보기) 위치를 회원에게 계정당 1회 알려준다.
// 노출 여부는 서버(users.guide_coachmark_seen_at)에 저장해 기기·PWA/브라우저가 달라도 다시 뜨지 않는다.
import React, { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { useLocation } from 'react-router-dom';
import api from '../api/axios';
import { setCoachmarkActive } from '../utils/coachmarkGate';

const TARGET_SELECTOR = '[data-coachmark="user-menu"]';
const PAD = 2; // 스포트라이트 여백
const RING = 2; // 금색 테두리 두께
const EDGE = 4; // 화면 가장자리와 최소 간격 (테두리 포함)

export default function GuideCoachmark() {
  const { pathname } = useLocation();
  const [show, setShow] = useState(false);
  const [rect, setRect] = useState(null);

  // 노출 여부 확인 — 확인하는 동안에도 설치 안내 카드 등은 대기
  useEffect(() => {
    let cancelled = false;
    setCoachmarkActive(true);
    api.get('/users/guide-coachmark')
      .then((res) => {
        if (cancelled) return;
        if (res.data?.show) setShow(true);
        else setCoachmarkActive(false);
      })
      .catch(() => { if (!cancelled) setCoachmarkActive(false); });
    return () => { cancelled = true; setCoachmarkActive(false); };
  }, []);

  const measure = useCallback(() => {
    const el = document.querySelector(TARGET_SELECTOR);
    if (!el) { setRect(null); return; }
    const r = el.getBoundingClientRect();
    // 대상 중심에 맞춘 원. 헤더 위쪽/오른쪽 가장자리를 넘지 않도록 지름을 줄인다
    const cx = r.left + r.width / 2;
    const cy = r.top + r.height / 2;
    const maxRadius = Math.min(cy, window.innerWidth - cx) - RING - EDGE;
    const radius = Math.max(12, Math.min(Math.max(r.width, r.height) / 2 + PAD, maxRadius));
    setRect({ top: cy - radius, left: cx - radius, width: radius * 2, height: radius * 2 });
  }, []);

  useLayoutEffect(() => {
    if (!show) return undefined;
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [show, measure, pathname]);

  const dismiss = useCallback((openMenu) => {
    setShow(false);
    setCoachmarkActive(false);
    api.post('/users/guide-coachmark/seen').catch(() => {});
    // 스포트라이트를 누른 경우 실제 메뉴를 열어 바로 기능 안내를 볼 수 있게 한다
    if (openMenu) document.querySelector(TARGET_SELECTOR)?.click();
  }, []);

  if (!show || !rect) return null;

  const viewportW = window.innerWidth;
  const bubbleTop = rect.top + rect.height + 14;
  const bubbleRight = Math.max(12, viewportW - (rect.left + rect.width));
  const arrowRight = viewportW - (rect.left + rect.width / 2) - bubbleRight - 7;

  return (
    <Box
      role="dialog"
      aria-label="사용자 메뉴 안내"
      // 바깥을 실수로 눌러 그냥 사라지지 않도록, 닫기는 [확인] 또는 스포트라이트 탭으로만
      sx={{ position: 'fixed', inset: 0, zIndex: (t) => t.zIndex.modal + 50 }}
    >
      {/* 스포트라이트 — 큰 그림자로 주변을 어둡게 덮고 대상만 뚫어 보인다 */}
      <Box
        onClick={(e) => { e.stopPropagation(); dismiss(true); }}
        sx={{
          position: 'fixed',
          top: rect.top, left: rect.left, width: rect.width, height: rect.height,
          borderRadius: '50%',
          boxShadow: '0 0 0 9999px rgba(0,0,0,0.6)',
          outline: `${RING}px solid`,
          outlineColor: 'secondary.main',
          cursor: 'pointer',
        }}
      />

      <Box
        onClick={(e) => e.stopPropagation()}
        sx={{
          position: 'fixed',
          top: bubbleTop,
          right: bubbleRight,
          width: 'min(280px, calc(100vw - 24px))',
          bgcolor: 'background.paper',
          color: 'text.primary',
          borderRadius: 2,
          p: 2,
          boxShadow: 6,
        }}
      >
        <Box
          sx={{
            position: 'absolute', top: -7, right: Math.max(12, arrowRight),
            width: 14, height: 14, bgcolor: 'background.paper', transform: 'rotate(45deg)',
          }}
        />
        <Typography sx={{ fontWeight: 700, fontSize: '1rem', mb: 0.5 }}>
          여기를 눌러 보세요
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5, lineHeight: 1.6 }}>
          피아노울림에서 쓸 수 있는 기능을 한눈에 볼 수 있어요.
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="contained" size="small" onClick={() => dismiss(false)} disableElevation>
            확인
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
